/**
 * The app shell (A+): sidebar on the left, a light top bar with search,
 * then the page.
 *
 * On a phone the sidebar becomes a drawer behind the top bar's menu button,
 * and the bottom tabs stay for the four things done most by thumb.
 */
import { useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { Box, Drawer } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useHotkeys } from '../hotkeys/HotkeyProvider'
import { useSmartBack } from '../hooks/useSmartBack'
import { c } from '../theme'
import PageBar from './PageBar'
import Sidebar, { SIDEBAR_WIDTH, SidebarContent } from './Sidebar'
import TopBar from './TopBar'
import CommandPalette, { useCommandPalette } from './CommandPalette'
import BottomNav from '../mobile/BottomNav'
import { UnsavedChangesProvider, useGuard } from '../store/UnsavedChanges'
import InstallPrompt from '../mobile/InstallPrompt'
import OfflineBanner from '../mobile/OfflineBanner'

export default function AppLayout() {
  return (
    <UnsavedChangesProvider>
      <Shell />
    </UnsavedChangesProvider>
  )
}

function Shell() {
  const { t } = useTranslation()
  const nav = useNavigate()
  const { back } = useSmartBack()
  const [drawer, setDrawer] = useState(false)
  const palette = useCommandPalette()
  const guard = useGuard()

  useHotkeys('layout', [
    { combo: 'alt+h', label: t('nav.home'), handler: () => guard(() => nav('/')) },
    // Not allowInInput: Escape while typing in a report's date filter
    // should dismiss the field, not navigate the user off the screen.
    { combo: 'escape', label: t('common.back'), handler: () => guard(back) },
  ])

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: c.surfacePage }}>
      <Sidebar />

      <Drawer open={drawer} onClose={() => setDrawer(false)}
        PaperProps={{ sx: { width: SIDEBAR_WIDTH + 24, maxWidth: '86vw',
                            bgcolor: c.surface, backgroundImage: 'none' } }}>
        <SidebarContent onNavigate={() => setDrawer(false)} />
      </Drawer>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <OfflineBanner />
        <TopBar onMenu={() => setDrawer(true)} onSearch={() => palette.setOpen(true)} />
        <PageBar />

        <Box component="main" sx={{
          width: '100%', maxWidth: 1360, mx: 'auto',
          px: { xs: 1.5, sm: 3, md: 4 }, pt: { xs: 1.5, sm: 3 },
          // Room for the bottom bar plus the home indicator.
          pb: { xs: 'calc(72px + env(safe-area-inset-bottom))', md: 4 },
        }}>
          <Outlet />
        </Box>
      </Box>

      <CommandPalette open={palette.open} onClose={() => palette.setOpen(false)} />
      <BottomNav />
      <InstallPrompt />
    </Box>
  )
}
